import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Autocomplete,
  DialogActions,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import * as React from "react";

import { useAuth } from "@/providers";
import { RobotoStyledTabs, RobotoStyledTab } from "@/shared/components";
import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  CollectionRecord,
  CollectionResourceRef,
} from "@/shared/domain/collections";
import { useAddToCollection } from "@/shared/domain/collections/hooks/useAddToCollection";
import { FileNode } from "@/shared/domain/files/filesystem";
import { fetchAllItems } from "@/shared/services/apiHooks";
import { RobotoAPICall, SearchQueryBody } from "@/types";

import styles from "./AddToCollection.module.css";
import { CreateCollection } from "./CreateCollection";

interface TabPanelProps {
  index: number;
  value: number;
  children: React.ReactElement;
}

export const TabPanel: React.FC<TabPanelProps> = ({
  index,
  value,
  children,
  ...props
}) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...props}
    >
      {value === index && (
        <div className={styles.tabPanelContent}>{children}</div>
      )}
    </div>
  );
};

interface AddToCollectionProps {
  selectedDatasets?: Set<string>;
  selectedFiles?: FileNode[];
  selectedEvents?: Set<string>;
  close: () => void;
}

export const AddToCollection: React.FC<AddToCollectionProps> = ({
  selectedDatasets,
  selectedFiles,
  selectedEvents,
  close,
}) => {
  const [collections, setCollections] = useState<CollectionRecord[]>([]);
  const [collection, setCollection] = useState<CollectionRecord | null>();
  const [tabValue, setTabValue] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [addToExistingError, setAddToExistingError] =
    React.useState<Error | null>(null);
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();

  const { mutate: addToCollection } = useAddToCollection();

  const updateCollection = () => {
    if (!collection?.collection_id || !currentOrganization?.org_id) {
      return;
    }

    const datasets =
      (selectedDatasets &&
        Array.from(selectedDatasets).map((dataset) => ({
          resource_type: "dataset" as const,
          resource_id: dataset,
        }))) ||
      [];

    const files =
      (selectedFiles &&
        selectedFiles.map((item) => ({
          resource_type: "file" as const,
          resource_id: item.file?.file_id || "",
        }))) ||
      [];

    const events =
      (selectedEvents &&
        Array.from(selectedEvents).map((id) => ({
          resource_type: "event" as const,
          resource_id: id,
        }))) ||
      [];

    let resources: CollectionResourceRef[] = datasets;
    if (files.length) {
      resources = files;
    }
    if (events.length) {
      resources = events;
    }

    setIsLoading(true);

    addToCollection(
      {
        collectionId: collection.collection_id,
        resourceOwnerId: currentOrganization.org_id,
        resources,
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          showNotification("Added to Collection");
          close();
        },
        onError: (error) => {
          setIsLoading(false);
          setAddToExistingError(error);
        },
      },
    );
  };

  React.useEffect(() => {
    async function fetchCollections() {
      setIsLoading(true);
      setAddToExistingError(null);

      const requestBody: SearchQueryBody = {
        sort_direction: "DESC",
        sort_by: "created",
      };

      const queryParams = new URLSearchParams();
      queryParams.set("content_mode", "summary_only");

      const collectionsQuery: RobotoAPICall = {
        endpoint: () => "/collections/search",
        method: "POST",
        orgId: currentOrganization?.org_id,
        requestBody: JSON.stringify(requestBody),
        queryParams: queryParams,
      };

      const collectionsQueryResponse =
        await fetchAllItems<CollectionRecord>(collectionsQuery);

      if (collectionsQueryResponse.error) {
        setAddToExistingError(collectionsQueryResponse.error);
        return;
      }

      setCollections(collectionsQueryResponse.items ?? []);

      setIsLoading(false);
    }

    void fetchCollections();
  }, [currentOrganization?.org_id]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  let errorDisplay = null;
  if (addToExistingError) {
    errorDisplay = (
      <Alert onClose={() => setAddToExistingError(null)} severity="error">
        {addToExistingError.message}
      </Alert>
    );
  }

  return (
    <form className={styles.form}>
      <Typography
        variant="subtitle1"
        gutterBottom
        component="div"
        className={styles.title}
      >
        Add to Collection
      </Typography>
      <Divider />
      <RobotoStyledTabs
        variant="fullWidth"
        value={tabValue}
        onChange={handleTabChange}
        aria-label="add-to-collection-tabs"
        className={styles.tabs}
      >
        <RobotoStyledTab label={"Existing Collection"} />
        <RobotoStyledTab label={"New Collection"} />
      </RobotoStyledTabs>

      <TabPanel value={tabValue} index={0}>
        <div className={styles.tabForm}>
          {errorDisplay}
          <Autocomplete
            id="available-collections"
            options={collections}
            getOptionLabel={(option) => option.name || option.collection_id}
            value={collection}
            size="small"
            loading={isLoading}
            onChange={(_, selection: CollectionRecord | null) => {
              setCollection(selection);
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Collection" />
            )}
            componentsProps={{
              paper: {
                className: styles.autocompletePaper,
              },
            }}
          />
          <DialogActions className={styles.dialogActions}>
            <LoadingButton
              loading={isLoading}
              type="submit"
              disabled={
                !collection ||
                ((!selectedEvents || selectedEvents.size === 0) &&
                  (!selectedFiles || selectedFiles.length === 0) &&
                  (!selectedDatasets || selectedDatasets.size === 0))
              }
              onClick={() => {
                void updateCollection();
              }}
              variant="outlined"
              style={{
                textTransform: "none",
              }}
              size="small"
            >
              Add to Collection (
              {selectedEvents?.size ??
                selectedFiles?.length ??
                selectedDatasets?.size ??
                0}
              )
            </LoadingButton>
          </DialogActions>
        </div>
      </TabPanel>
      <TabPanel value={tabValue} index={1}>
        <div className={styles.tabForm}>
          <CreateCollection
            selectedDatasets={selectedDatasets}
            selectedFiles={selectedFiles}
            selectedEvents={selectedEvents}
            close={close}
          />
        </div>
      </TabPanel>
    </form>
  );
};
